"use client";

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Music, X, Send, Clock, AlertCircle, Globe, Users, Lock, Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/ui/use-toast";
import {
  useTikTokAccounts,
  useTikTokActions,
  type TikTokAccount,
} from "@/lib/hooks/useTikTok";
import { useLanguageNavigate } from "@/lib/hooks/useLanguageNavigate";

interface TikTokPublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  videoTitle: string;
  onPublished?: () => void;
}

const PRIVACY_OPTIONS = [
  { value: "PUBLIC_TO_EVERYONE", icon: Globe },
  { value: "FOLLOWER_OF_CREATOR", icon: Users },
  { value: "MUTUAL_FOLLOW_FRIENDS", icon: Users },
  { value: "SELF_ONLY", icon: Lock },
] as const;

export function TikTokPublishDialog({
  open,
  onOpenChange,
  jobId,
  videoTitle,
  onPublished,
}: TikTokPublishDialogProps) {
  const { t } = useTranslation();
  const navigate = useLanguageNavigate();
  const { data: accountsData, isLoading: accountsLoading } = useTikTokAccounts();
  const actions = useTikTokActions();

  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [caption, setCaption] = useState("");
  const [hashtagInput, setHashtagInput] = useState("");
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [privacyLevel, setPrivacyLevel] = useState<string>("SELF_ONLY");
  const [disableDuet, setDisableDuet] = useState(false);
  const [disableStitch, setDisableStitch] = useState(false);
  const [disableComment, setDisableComment] = useState(false);
  const [isCommercialContent, setIsCommercialContent] = useState(false);
  const [isAiGenerated, setIsAiGenerated] = useState(true);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  const accounts = accountsData?.accounts || [];
  const maxCaption = 2200;
  const maxHashtags = 100;

  // Auto-select first account
  useEffect(() => {
    if (accounts.length > 0 && !selectedAccountId) {
      setSelectedAccountId(accounts[0]!.id);
    }
  }, [accounts, selectedAccountId]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setCaption("");
      setHashtags([]);
      setHashtagInput("");
      setPrivacyLevel("SELF_ONLY");
      setDisableDuet(false);
      setDisableStitch(false);
      setDisableComment(false);
      setIsCommercialContent(false);
      setIsAiGenerated(true);
      setIsScheduled(false);
      setScheduledAt("");
      setIsPublishing(false);
    }
  }, [open]);

  const addHashtag = () => {
    const tag = hashtagInput.trim().replace(/^#/, "");
    if (!tag) return;
    if (hashtags.length >= maxHashtags) return;
    if (hashtags.includes(tag)) return;
    setHashtags([...hashtags, tag]);
    setHashtagInput("");
  };

  const removeHashtag = (tag: string) => {
    setHashtags(hashtags.filter((h) => h !== tag));
  };

  const handleHashtagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addHashtag();
    }
  };

  const handlePublish = async () => {
    if (!selectedAccountId || !jobId) return;

    setIsPublishing(true);
    try {
      await actions.publish({
        jobId,
        accountId: selectedAccountId,
        caption,
        hashtags,
        privacyLevel,
        disableDuet,
        disableStitch,
        disableComment,
        isCommercialContent,
        isAiGenerated,
        ...(isScheduled && scheduledAt ? { scheduledAt: new Date(scheduledAt).toISOString() } : {}),
      });

      toast({
        title: isScheduled ? t("tiktok.scheduled") : t("tiktok.publishing"),
        description: isScheduled ? t("tiktok.scheduledDesc") : t("tiktok.publishingDesc"),
      });

      onPublished?.();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: t("tiktok.publishError"),
        description: error instanceof Error ? error.message : t("errors.defaultMessage"),
        variant: "destructive",
      });
    } finally {
      setIsPublishing(false);
    }
  };

  // Minimum schedule time: 10 minutes from now
  const minScheduleTime = new Date(Date.now() + 10 * 60 * 1000)
    .toISOString()
    .slice(0, 16);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Music className="w-5 h-5" />
            {t("tiktok.publishToTikTok")}
          </DialogTitle>
          <DialogDescription>
            {t("tiktok.publishDialogDesc", { title: videoTitle })}
          </DialogDescription>
        </DialogHeader>

        {accountsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : accounts.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <AlertCircle className="w-10 h-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground max-w-[220px]">
              {t("tiktok.noAccountsConnect")}
            </p>
            <Button
              size="sm"
              className="gap-2 bg-black hover:bg-black/90 text-white"
              onClick={() => {
                onOpenChange(false);
                navigate("/account#tiktok");
              }}
            >
              <Music className="w-3.5 h-3.5" />
              {t("tiktok.connectAccount")}
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Account selector */}
            <div className="space-y-2">
              <Label>{t("tiktok.account")}</Label>
              <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("tiktok.selectAccount")} />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      <div className="flex items-center gap-2">
                        {account.avatarUrl && !imageErrors[account.id] ? (
                          <img
                            src={account.avatarUrl}
                            alt=""
                            onError={() => setImageErrors((prev) => ({ ...prev, [account.id]: true }))}
                            className="w-5 h-5 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-black flex items-center justify-center">
                            <Music className="w-3 h-3 text-white" />
                          </div>
                        )}
                        {account.tiktokUsername ? `@${account.tiktokUsername}` : account.displayName}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Caption */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t("tiktok.caption")}</Label>
                <span className="text-xs text-muted-foreground">
                  {caption.length}/{maxCaption}
                </span>
              </div>
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value.slice(0, maxCaption))}
                placeholder={t("tiktok.captionPlaceholder")}
                rows={3}
                className="resize-none"
              />
            </div>

            {/* Hashtags */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t("tiktok.hashtags")}</Label>
                <span className="text-xs text-muted-foreground">
                  {hashtags.length}/{maxHashtags}
                </span>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={hashtagInput}
                  onChange={(e) => setHashtagInput(e.target.value)}
                  onKeyDown={handleHashtagKeyDown}
                  placeholder={t("tiktok.hashtagPlaceholder")}
                  className="flex-1 px-3 py-2 text-sm border rounded-md bg-background"
                  disabled={hashtags.length >= maxHashtags}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addHashtag}
                  disabled={!hashtagInput.trim() || hashtags.length >= maxHashtags}
                >
                  {t("tiktok.addHashtag")}
                </Button>
              </div>
              {hashtags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {hashtags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="gap-1 cursor-pointer hover:bg-destructive/10"
                      onClick={() => removeHashtag(tag)}
                    >
                      #{tag}
                      <X className="w-3 h-3" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Privacy level */}
            <div className="space-y-2">
              <Label>{t("tiktok.privacyLevel")}</Label>
              <Select value={privacyLevel} onValueChange={setPrivacyLevel}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIVACY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex items-center gap-2">
                        <opt.icon className="w-4 h-4" />
                        {t(`tiktok.privacy.${opt.value}`)}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Interaction toggles */}
            <div className="space-y-2">
              <Label>{t("tiktok.interactionSettings")}</Label>
              <div className="space-y-2 pl-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={disableDuet}
                    onChange={(e) => setDisableDuet(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  {t("tiktok.disableDuet")}
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={disableStitch}
                    onChange={(e) => setDisableStitch(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  {t("tiktok.disableStitch")}
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={disableComment}
                    onChange={(e) => setDisableComment(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  {t("tiktok.disableComment")}
                </label>
              </div>
            </div>

            {/* Content disclosure */}
            <div className="space-y-2">
              <Label>{t("tiktok.contentDisclosure")}</Label>
              <div className="space-y-2 pl-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isCommercialContent}
                    onChange={(e) => setIsCommercialContent(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  {t("tiktok.commercialContent")}
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isAiGenerated}
                    onChange={(e) => setIsAiGenerated(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  {t("tiktok.aiGenerated")}
                </label>
                <p className="text-xs text-muted-foreground">{t("tiktok.aiGeneratedNote")}</p>
              </div>
            </div>

            {/* Schedule toggle */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="tiktok-schedule-toggle"
                  checked={isScheduled}
                  onChange={(e) => setIsScheduled(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="tiktok-schedule-toggle" className="cursor-pointer">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    {t("tiktok.schedulePost")}
                  </div>
                </Label>
              </div>
              {isScheduled && (
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  min={minScheduleTime}
                  className="w-full px-3 py-2 text-sm border rounded-md bg-background"
                />
              )}
            </div>

            {/* Publish button */}
            <Button
              onClick={handlePublish}
              disabled={isPublishing || !selectedAccountId}
              className="w-full bg-black hover:bg-gray-800 text-white gap-2"
            >
              {isPublishing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isScheduled ? (
                <Clock className="w-4 h-4" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {isPublishing
                ? t("tiktok.publishingAction")
                : isScheduled
                  ? t("tiktok.scheduleAction")
                  : t("tiktok.publishAction")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
